import { useTranslation } from 'react-i18next';
import { DynamicCombobox } from '@shared/components/ui/DynamicCombobox';
import { useTenantChildDynamicCombobox } from '@shared/hooks/useTenantChildDynamicCombobox';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { ClientSelectorSkeleton } from '../skeletons/ClientSelectorSkeleton';

interface ClientSelectorProps {
  parentTeamId: number | undefined;
  /** Cargar listado solo cuando aplique (p. ej. 3PL con team válido). */
  comboboxEnabled: boolean;
  selectedTenantChildId: number | null;
  onSelectTenantChild: (value: string) => void;
  isDisabled?: boolean;
  isLocked?: boolean;
  /** Modo del componente: 'create' para creación, 'edit' para edición */
  mode?: 'create' | 'edit';
}

/**
 * Componente para seleccionar el cliente cuando el usuario es 3PL.
 * Lista paginada con búsqueda y scroll infinito (DynamicCombobox + /teams-child).
 */
export const ClientSelector = ({
  parentTeamId,
  comboboxEnabled,
  selectedTenantChildId,
  onSelectTenantChild,
  isDisabled = false,
  isLocked = false,
  mode = 'create',
}: ClientSelectorProps) => {
  const { t } = useTranslation('createEditProductsModule');

  const {
    searchFn,
    onLoadMore,
    hasMore,
    isLoadingBrowse,
    isFetchedBrowse,
    browseTotal,
    resolveSelectedOption,
    fetchError,
  } = useTenantChildDynamicCombobox({
    parentTeamId,
    enabled: comboboxEnabled,
  });

  const translationKey = mode === 'edit' ? 'clientSelectorEdit' : 'clientSelector';

  const hasClients = (browseTotal ?? 1) > 0;
  const listEmptyAfterFetch = Boolean(
    comboboxEnabled && isFetchedBrowse && browseTotal !== undefined && browseTotal === 0,
  );

  const showInitialSkeleton = comboboxEnabled && isLoadingBrowse && mode === 'create' && !selectedTenantChildId;

  const displayError = fetchError ?? null;

  if (showInitialSkeleton) {
    return <ClientSelectorSkeleton />;
  }

  return (
    <div className="border border-card-border rounded-lg p-4 mb-6 shadow-sm">
      <div>
        <h2 className="text-lg font-semibold text-title">{t(`${translationKey}.title`)}</h2>
        {mode === 'create' && t(`${translationKey}.description`) && (
          <p className="text-sm text-complementary-text mt-1">{t(`${translationKey}.description`)}</p>
        )}
      </div>

      <div className="mt-4">
        <DynamicCombobox
          value={selectedTenantChildId ? String(selectedTenantChildId) : ''}
          setValue={onSelectTenantChild}
          placeholder={
            comboboxEnabled && hasClients ? t(`${translationKey}.placeholder`) : t(`${translationKey}.empty`)
          }
          searchFn={searchFn}
          resolveSelectedOption={resolveSelectedOption}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          className="w-full"
          disabled={isDisabled || !comboboxEnabled || listEmptyAfterFetch}
          dataTestId="client-selector-combobox"
        />
      </div>

      <div className="mt-4 space-y-2">
        {displayError && (
          <Alert className="border-error text-error">
            <AlertDescription>{displayError}</AlertDescription>
          </Alert>
        )}
        {listEmptyAfterFetch && !displayError && (
          <Alert className="border-warning text-warning">
            <AlertDescription>{t(`${translationKey}.emptyDescription`)}</AlertDescription>
          </Alert>
        )}
        {isLocked && comboboxEnabled && hasClients && !isDisabled && (
          <div className="flex items-center gap-3 rounded-lg border border-card-border bg-card-bg px-4 py-4 text-sm text-title">
            <FontAwesomeIcon icon={faCircleInfo} className="w-4 h-4 text-icon" />
            <span>{t(`${translationKey}.lockedMessage`)}</span>
          </div>
        )}
      </div>
    </div>
  );
};
